import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import { StoreState } from "../redux/reducers";
import {
  fetchSubModules,
  addSubModule,
  editSubModule,
  deleteSubModuleById,
  fetchSubModulesByProject,
} from "../redux/actions/subModuleAction";
import { SubModule } from "../types/SubModule";
import SubModuleForm from "../components/SubModuleForm";
import SubModuleList from "../components/SubModuleList";
import Modal from "../components/Modal";
import CollapseCard from "../components/custom/CollapseCard";
import PageContainer from "../components/custom/PageContainer";
import Main from "../components/custom/Main";
import { useToast } from "../components/toast/ToastProvider";
import SubModuleFilter from "../components/SubModuleFilter";

const SubModulePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();

  const subModules = useSelector((state: StoreState) => state.subModule);
  const [selected, setSelected] = useState<SubModule | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSave = async (mod: SubModule) => {
    setLoading(true);
    try {
      if (selected) {
        await dispatch(editSubModule(mod.id, mod));
        toast("SubModule updated successfully");
      } else {
        await dispatch(addSubModule(mod));
        toast("SubModule added successfully");
      }
      dispatch(fetchSubModules());
      setSelected(null);
      setShowModal(false);
    } catch (error) {
      toast("Error saving module");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this module?")) {
      setLoading(true);
      try {
        await dispatch(deleteSubModuleById(id));
        toast("SubModule deleted successfully");
      } catch (error) {
        toast("Error deleting module");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (mod: SubModule) => {
    setSelected(mod);
    setShowModal(true);
  };

  useEffect(() => {
    setLoading(true);
    dispatch(fetchSubModules()).finally(() => setLoading(false));
  }, [dispatch]);

  const [filters, setFilters] = useState({
    project: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [isFiltered, setIsFiltered] = useState(false);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleFindFilters = () => {
    setAppliedFilters(filters);
    setIsFiltered(true);
    localStorage.setItem("subModuleFilter", JSON.stringify(filters));
    if (filters.project) {
      dispatch(fetchSubModulesByProject(parseInt(filters.project)));
    } else {
      dispatch(fetchSubModules());
    }
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      project: "",
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setIsFiltered(false);
    localStorage.removeItem("subModuleFilter");
  };
  useEffect(() => {
    const savedFilters = localStorage.getItem("subModuleFilter");
    if (savedFilters) {
      const parsed = JSON.parse(savedFilters);
      setFilters(parsed);
      setAppliedFilters(parsed);
      setIsFiltered(true);
    }
  }, []);

  return (
    <Main>
      <PageContainer
        title="Sub-Module Management"
        buttonText={loading ? "Loading..." : "ADD"}
        onButtonClick={() => setShowModal(true)}
      >
        <Modal
          visible={showModal}
          size="xl"
          onClose={() => {
            setShowModal(false);
            setSelected(null);
          }}
          headerText={selected ? "Update Sub-Module" : "Add Sub-Module"}
        >
          <SubModuleForm
            initialData={selected}
            onSave={handleSave}
            clearAfterSubmit={() => setSelected(null)}
          />
        </Modal>
        <CollapseCard title="Sub-Module Filter">
          <SubModuleFilter
            filters={filters}
            onFilterChange={handleFilterChange}
            onFind={handleFindFilters}
            onReset={handleResetFilters}
          />
        </CollapseCard>
        <CollapseCard title="Sub-Module List">
          <SubModuleList
            subModules={subModules}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};

export default SubModulePage;
